import React, { useState } from 'react';
import { makeStyles } from '@mui/styles';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import InputAdornment from '@mui/material/InputAdornment';
import Chip from '@mui/material/Chip';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Collapse from '@mui/material/Collapse';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import axios from '../utils/axiosConfig';

// Super Admin > Proctoring Insights: enter a candidate's email and get, per attempt, the proctoring
// record this server holds and a synopsis: what most likely happened, why, and the evidence
// (backend: POST /admin/proctoringFacts, utils/proctoringSynopsis.js).
const useStyles = makeStyles(() => ({
    content: {
        flexGrow: 1,
        padding: '28px 32px',
        width: '100%',
        boxSizing: 'border-box',
        '@media (max-width: 600px)': { padding: '12px 10px' },
    },
    toolbar: { minHeight: 56 },
    pageTitle: { fontSize: '1.5rem', fontWeight: 700, color: '#1a1a2e' },
    pageSubtitle: { fontSize: '0.875rem', color: '#8a8a9a', marginBottom: '20px' },
    searchRow: { display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' },
    searchButton: {
        backgroundColor: '#6f2981 !important',
        borderRadius: '10px !important',
        textTransform: 'none !important',
        minWidth: '110px !important',
    },
    card: { borderRadius: '14px !important', marginBottom: '18px', boxShadow: '0 1px 4px rgba(0,0,0,0.08) !important' },
    attemptTitle: { fontWeight: 700, fontSize: '1.05rem', color: '#1a1a2e' },
    meta: { fontSize: '0.8rem', color: '#6b6b7b' },
    sectionTitle: { fontWeight: 600, fontSize: '0.85rem', color: '#6f2981', margin: '14px 0 6px' },
    grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(190px, 1fr))', gap: '6px 16px' },
    fact: { fontSize: '0.82rem', color: '#333' },
    factLabel: { color: '#8a8a9a' },
    headline: { fontSize: '0.95rem', fontWeight: 600, padding: '10px 14px', borderRadius: '10px', margin: '12px 0 4px' },
    story: { border: '1px solid', borderRadius: '10px', padding: '10px 14px', marginTop: '10px' },
    storyTitle: { fontWeight: 700, fontSize: '0.92rem' },
    storyText: { fontSize: '0.85rem', color: '#333', marginTop: '2px' },
    list: { margin: '2px 0 4px', paddingLeft: '20px', fontSize: '0.83rem', color: '#444' },
    link: { cursor: 'pointer', color: '#6f2981', fontSize: '0.82rem', fontWeight: 600, marginTop: '8px', display: 'inline-block' },
}));

const LEVEL_STYLE = {
    problem: { background: '#fdecea', color: '#8a1c12', border: '#f5c2bd' },
    attention: { background: '#fff4e5', color: '#7a4a00', border: '#f3d19c' },
    info: { background: '#eef3fb', color: '#1f3b63', border: '#c9d8ef' },
    ok: { background: '#edf7ed', color: '#1e4620', border: '#bfdcc0' },
};
const VERDICT_LEVEL = { problem: 'problem', attention: 'attention', clean: 'ok' };

// One thing that happened in the attempt: what, why, how sure, and the evidence.
const Story = ({ story }) => {
    const classes = useStyles();
    const st = LEVEL_STYLE[story.level] || LEVEL_STYLE.info;
    return (
        <Box className={classes.story} style={{ borderColor: st.border }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 0.5 }}>
                <Typography className={classes.storyTitle} style={{ color: st.color }}>{story.title}</Typography>
                <Chip size="small" label={story.confidence} sx={{ height: 20, fontSize: '0.7rem', background: st.background, color: st.color }} />
            </Box>
            <Typography className={classes.storyText}><b>What happened:</b> {story.what}</Typography>
            <Typography className={classes.storyText}><b>Why:</b> {story.why}</Typography>
            {story.evidence.length > 0 && (
                <>
                    <Typography className={classes.storyText}><b>Evidence:</b></Typography>
                    <ul className={classes.list}>
                        {story.evidence.map((e, i) => <li key={i}>{e}</li>)}
                    </ul>
                </>
            )}
            {story.rules_out && story.rules_out.length > 0 && (
                <>
                    <Typography className={classes.storyText}><b>Ruled out:</b></Typography>
                    <ul className={classes.list}>
                        {story.rules_out.map((e, i) => <li key={i}>{e}</li>)}
                    </ul>
                </>
            )}
        </Box>
    );
};

const ist = (ms, withDate = true) => {
    if (!ms) return '-';
    return new Date(ms).toLocaleString('en-IN', {
        timeZone: 'Asia/Kolkata',
        ...(withDate ? { day: '2-digit', month: 'short', year: 'numeric' } : {}),
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false,
    });
};

const yesNo = (v) => (v ? 'On' : 'Off');

const Fact = ({ label, value }) => {
    const classes = useStyles();
    return (
        <Typography className={classes.fact}>
            <span className={classes.factLabel}>{label}: </span>
            {value === null || value === undefined || value === '' ? '-' : value}
        </Typography>
    );
};

const Attempt = ({ a }) => {
    const classes = useStyles();
    const [showTimeline, setShowTimeline] = useState(false);
    const [showSaved, setShowSaved] = useState(false);
    const s = a.settings;
    const statusColor = ['111', '113', '114', '112'].includes(a.status_code) ? 'error' : a.status_code === '010' ? 'success' : 'default';

    return (
        <Card className={classes.card}>
            <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap' }}>
                    <Box>
                        <Typography className={classes.attemptTitle}>{a.test_name}</Typography>
                        <Typography className={classes.meta}>
                            Attempt {a.userexams_id}
                            {a.org_name ? ` · ${a.org_name}` : ''}
                            {a.position_name ? ` · ${a.position_name}` : ''}
                        </Typography>
                        <Typography className={classes.meta}>
                            {ist(a.start_ms)} → {ist(a.end_ms, false)} IST · {a.minutes_taken ?? '-'} of {a.duration_minutes ?? '-'} min
                        </Typography>
                    </Box>
                    <Chip size="small" color={statusColor} label={a.status + (a.abort_reason ? ` (${a.abort_reason.replace(/_/g, ' ')})` : '')} />
                </Box>

                <Typography className={classes.sectionTitle}>Synopsis</Typography>
                <Box
                    className={classes.headline}
                    style={{ background: LEVEL_STYLE[VERDICT_LEVEL[a.synopsis.verdict]].background, color: LEVEL_STYLE[VERDICT_LEVEL[a.synopsis.verdict]].color }}
                >
                    {a.synopsis.headline}
                </Box>
                {a.synopsis.stories.map((st, i) => <Story key={i} story={st} />)}

                <Typography className={classes.sectionTitle} sx={{ mt: 2 }}>Details</Typography>

                <Typography className={classes.sectionTitle}>Test settings</Typography>
                <Box className={classes.grid}>
                    <Fact label="Camera monitoring" value={yesNo(s.monitoring)} />
                    <Fact label="Photo every" value={s.photo_timer ? `${s.photo_timer} s` : null} />
                    <Fact label="Photo matching" value={yesNo(s.photo_matching)} />
                    <Fact label="Photo action" value={s.photo_action === 'S' ? 'Stop test' : s.photo_action} />
                    <Fact label="Object detection" value={yesNo(s.object_detection)} />
                    <Fact label="Live monitoring" value={yesNo(s.live_monitoring)} />
                    <Fact label="Proctoring warnings limit" value={s.imageproctoringcount} />
                    <Fact label="Window switch limit" value={s.webproctoringcount} />
                    <Fact label="Speaker warnings limit" value={s.audioproctoringcount} />
                </Box>

                <Typography className={classes.sectionTitle}>Photos</Typography>
                <Box className={classes.grid}>
                    <Fact
                        label="Reference photo"
                        value={a.reference ? `${ist(a.reference.at_ms, false)} · ${a.reference.success ? 'OK' : 'failed'} · ${a.reference.face_count ?? '-'} face(s)` : 'none on record'}
                    />
                    <Fact label="Photo checks received" value={`${a.photos.answered} (expected ~${a.photos.expected})`} />
                    <Fact label="Per 10 minutes" value={a.photos.per_10_min} />
                    <Fact label="Other candidates, per 10 min" value={a.peers ? `${a.peers.checks_per_10_min ?? '-'} (${a.peers.attempts} attempts)` : null} />
                    <Fact label="Failed on our side" value={a.photos.failed} />
                    <Fact
                        label="Longest time without a photo"
                        value={a.photos.longest_gap ? `${a.photos.longest_gap.minutes} min (${ist(a.photos.longest_gap.from_ms, false)}–${ist(a.photos.longest_gap.to_ms, false)})` : null}
                    />
                    <Fact
                        label="Saved warning photos"
                        value={a.saved_photos.total ? Object.entries(a.saved_photos.by_type).map(([k, v]) => `${k} ${v}`).join(', ') : 'none'}
                    />
                </Box>
                {a.photos.timeline.length > 0 && (
                    <Typography className={classes.link} onClick={() => setShowTimeline(!showTimeline)}>
                        {showTimeline ? 'Hide' : 'Show'} photo checks ({a.photos.timeline.length})
                    </Typography>
                )}
                <Collapse in={showTimeline} unmountOnExit>
                    <TableContainer sx={{ maxHeight: 320, mt: 1 }}>
                        <Table size="small" stickyHeader>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Time (IST)</TableCell>
                                    <TableCell>HTTP</TableCell>
                                    <TableCell>Faces</TableCell>
                                    <TableCell>Match</TableCell>
                                    <TableCell>Similarity</TableCell>
                                    <TableCell>Objects</TableCell>
                                    <TableCell>Error</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {a.photos.timeline.map((c, i) => (
                                    <TableRow key={i}>
                                        <TableCell>{ist(c.at_ms, false)}</TableCell>
                                        <TableCell>{c.http_status ?? '-'}</TableCell>
                                        <TableCell>{c.face_count ?? '-'}</TableCell>
                                        <TableCell>{c.face_match === null || c.face_match === undefined ? '-' : c.face_match ? 'Yes' : 'No'}</TableCell>
                                        <TableCell>{c.similarity ?? '-'}</TableCell>
                                        <TableCell>{c.objects || '-'}</TableCell>
                                        <TableCell>{c.error_message || '-'}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Collapse>
                {a.saved_photos.list.length > 0 && (
                    <>
                        <br />
                        <Typography className={classes.link} onClick={() => setShowSaved(!showSaved)}>
                            {showSaved ? 'Hide' : 'Show'} saved warning photos ({a.saved_photos.list.length})
                        </Typography>
                    </>
                )}
                <Collapse in={showSaved} unmountOnExit>
                    <TableContainer sx={{ maxHeight: 260, mt: 1 }}>
                        <Table size="small" stickyHeader>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Time (IST)</TableCell>
                                    <TableCell>Type</TableCell>
                                    <TableCell>Faces</TableCell>
                                    <TableCell>Objects</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {a.saved_photos.list.map((p, i) => (
                                    <TableRow key={i}>
                                        <TableCell>{ist(p.at_ms, false)}</TableCell>
                                        <TableCell>{p.type || '-'}</TableCell>
                                        <TableCell>{p.face_count ?? '-'}</TableCell>
                                        <TableCell>{p.objects || '-'}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Collapse>

                <Typography className={classes.sectionTitle}>Warnings and switches</Typography>
                <Box className={classes.grid}>
                    <Fact label="Window switches used" value={a.window_switches.used !== null ? `${a.window_switches.used} of ${a.window_switches.limit}` : null} />
                    <Fact label="Face not found" value={a.violations.faceNotFound} />
                    <Fact label="Multiple faces" value={a.violations.multipleFaces} />
                    <Fact label="Object detected" value={a.violations.objectDetected} />
                    <Fact label="Photo mismatch" value={a.violations.photoMismatch} />
                    <Fact label="Multiple speakers" value={a.violations.audio} />
                </Box>

                <Typography className={classes.sectionTitle}>Audio</Typography>
                <Box className={classes.grid}>
                    <Fact label="Clips checked" value={a.audio.clips} />
                    <Fact label="Failed" value={a.audio.failed} />
                    <Fact label="Multiple-speaker alerts" value={a.audio.multiple_speaker_alerts} />
                    <Fact label="Talking" value={a.audio.talking} />
                    <Fact label="Reading aloud" value={a.audio.reading_aloud} />
                </Box>

                {a.integrity && (
                    <>
                        <Typography className={classes.sectionTitle}>Integrity score</Typography>
                        <Typography className={classes.fact}>
                            {a.integrity.overall_score} ({a.integrity.band})
                            {Array.isArray(a.integrity.sub_scores) &&
                                ' · ' +
                                    a.integrity.sub_scores
                                        .filter((x) => x.active && x.occurrences > 0)
                                        .map((x) => `${x.label}: ${x.occurrences}`)
                                        .join(', ')}
                        </Typography>
                    </>
                )}

                {a.saved_state && (
                    <Typography className={classes.meta} sx={{ mt: 1.5 }}>
                        Saved test data last updated {ist(a.saved_state.updated_ms)} IST
                    </Typography>
                )}
            </CardContent>
        </Card>
    );
};

const ProctoringFacts = () => {
    const classes = useStyles();
    const [email, setEmail] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [data, setData] = useState(null);

    const doSearch = async () => {
        const q = email.trim();
        if (!q) {
            setError('Enter an email address');
            return;
        }
        setLoading(true);
        setError('');
        setData(null);
        try {
            const res = await axios.post('/admin/proctoringFacts', { email: q });
            if (!res.data.success) {
                setError(res.data.message || 'Could not load proctoring facts');
            } else if (res.data.users.length === 0) {
                setError(`No candidate found with ${q} on this server.`);
            } else if (res.data.attempts.length === 0) {
                setError(`${q} has not started any test.`);
            } else {
                setData(res.data);
            }
        } catch (e) {
            setError((e.response && e.response.data && e.response.data.message) || 'Could not load proctoring facts');
        } finally {
            setLoading(false);
        }
    };

    return (
        <main className={classes.content}>
            <div className={classes.toolbar} />
            <Typography className={classes.pageTitle}>Proctoring Insights</Typography>
            <Typography className={classes.pageSubtitle}>
                What most likely happened in a candidate's tests on this server, why, and the evidence - latest 20 attempts
            </Typography>

            <Card className={classes.card}>
                <CardContent>
                    <Box className={classes.searchRow}>
                        <TextField
                            size="small"
                            placeholder="Candidate email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            onKeyDown={(e) => e.key === 'Enter' && doSearch()}
                            sx={{ flex: 1, minWidth: 220 }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <FactCheckIcon sx={{ color: '#6f2981', opacity: 0.6 }} />
                                    </InputAdornment>
                                ),
                            }}
                        />
                        <Button variant="contained" onClick={doSearch} disabled={loading} className={classes.searchButton} disableElevation>
                            {loading ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : 'Analyse'}
                        </Button>
                    </Box>
                </CardContent>
            </Card>

            {error && (
                <Alert severity="info" sx={{ mb: 2.5, borderRadius: '12px' }}>
                    {error}
                </Alert>
            )}

            {data && data.attempts.map((a) => <Attempt key={a.userexams_id} a={a} />)}
        </main>
    );
};

export default ProctoringFacts;
